import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
} from "recharts";
import {
  BookOpen,
  Users,
  Calendar,
  Layers,
  FileText,
  Award,
  CheckCircle2,
  TrendingUp,
  ChevronDown,
  Building,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { getElearningDashboard } from "../../api/elearning/dashboard";
import { getAllBranches_Array } from "../../api/hrm/branches/info";

const COLORS = ["#6366f1", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"];

const fmtPct = (v) =>
  v == null || Number.isNaN(v) ? "—" : `${Number(v).toLocaleString("vi-VN")}%`;

const fmtDT = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

export default function ElearningDashboard() {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const years = Array.from({ length: 8 }, (_, i) => new Date().getFullYear() - i);

  const [selectedBranch, setSelectedBranch] = useState("all");
  const [branches, setBranches] = useState([{ id: "all", name: "Tất cả chi nhánh" }]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dash, setDash] = useState(null);

  useEffect(() => {
    getAllBranches_Array().then((list) => {
      const opts = [
        { id: "all", name: "Tất cả chi nhánh" },
        ...(Array.isArray(list) ? list : []).map((b) => ({
          id: String(b.id),
          name: b.name || b.code || String(b.id),
        })),
      ];
      setBranches(opts);
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        year: selectedYear,
        ...(selectedBranch !== "all" ? { branch_id: selectedBranch } : {}),
      };
      const data = await getElearningDashboard(params);
      setDash(data);
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.message || "Không tải được dữ liệu dashboard.");
      setDash(null);
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedBranch]);

  useEffect(() => {
    load();
  }, [load]);

  const stats = dash?.stats;
  const enrollmentByMonth = dash?.enrollment_by_month || [];
  const bySubject = dash?.classes_by_subject || [];
  const recent = dash?.recent_enrollments || [];
  const scheduleToday = dash?.schedule_today || [];

  const kpi = [
    {
      label: "Học viên (đăng ký lớp trong phạm vi)",
      value: stats?.students_active ?? "—",
      icon: Users,
      color: "bg-blue-50",
      text: "text-blue-600",
      hint: "Theo lớp giao với năm & chi nhánh",
    },
    {
      label: "Khóa học (có lớp trong phạm vi)",
      value: stats?.courses_open ?? "—",
      icon: BookOpen,
      color: "bg-indigo-50",
      text: "text-indigo-600",
      hint: "Distinct course_id từ lớp lọc được",
    },
    {
      label: "Lớp đang áp dụng bộ lọc",
      value: stats?.classes_active ?? "—",
      icon: Layers,
      color: "bg-emerald-50",
      text: "text-emerald-600",
      hint: "Lớp hoạt động, giao khoảng thời gian với năm",
    },
    {
      label: "Chứng nhận đã cấp (theo năm)",
      value: stats?.certificates_issued ?? "—",
      icon: Award,
      color: "bg-amber-50",
      text: "text-amber-600",
      hint: "Ngày cấp trong năm; lọc chi nhánh qua lớp/khóa",
    },
    {
      label: "Tỉ lệ điểm danh (có mặt / muộn)",
      value: fmtPct(stats?.attendance_rate_pct),
      icon: CheckCircle2,
      color: "bg-rose-50",
      text: "text-rose-600",
      hint: "Buổi điểm danh trong năm",
    },
    {
      label: "Tài liệu (toàn hệ thống)",
      value: stats?.documents_total ?? "—",
      icon: FileText,
      color: "bg-slate-50",
      text: "text-slate-600",
      hint: "Không gắn chi nhánh trên metadata",
    },
  ];

  const branchLabel =
    selectedBranch === "all"
      ? "Tất cả chi nhánh"
      : branches.find((b) => b.id === selectedBranch)?.name || "Chi nhánh";

  return (
    <div className="p-6 bg-gray-50/50 min-h-screen space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-gray-800 uppercase tracking-tight leading-none">
            Dashboard E-Learning
          </h1>
          <p className="text-gray-500 font-medium mt-2 text-sm">
            Số liệu từ cơ sở dữ liệu — lọc theo chi nhánh (theo lớp) và năm dương lịch
          </p>
        </div>

        <div className="flex flex-wrap md:flex-nowrap gap-3 text-sm">
          <div className="relative group min-w-[200px]">
            <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none text-red-600">
              <Building size={16} strokeWidth={3} />
            </div>
            <select
              value={selectedBranch}
              onChange={(e) => setSelectedBranch(e.target.value)}
              disabled={loading}
              className="w-full appearance-none pl-11 pr-12 py-3 bg-white rounded-2xl shadow-sm border border-gray-100 font-black text-gray-700 outline-none focus:ring-2 focus:ring-red-100 transition-all cursor-pointer hover:border-red-200 disabled:opacity-60"
            >
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none text-gray-400 group-hover:text-red-600 transition-colors">
              <ChevronDown size={16} strokeWidth={3} />
            </div>
          </div>

          <div className="relative group min-w-[140px]">
            <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none text-red-600">
              <Calendar size={16} strokeWidth={3} />
            </div>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
              disabled={loading}
              className="w-full appearance-none pl-11 pr-12 py-3 bg-white rounded-2xl shadow-sm border border-gray-100 font-black text-gray-700 outline-none focus:ring-2 focus:ring-red-100 transition-all cursor-pointer hover:border-red-200 disabled:opacity-60"
            >
              {years.map((year) => (
                <option key={year} value={year}>
                  Năm {year}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none text-gray-400 group-hover:text-red-600 transition-colors">
              <ChevronDown size={16} strokeWidth={3} />
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-3 p-4 rounded-2xl bg-rose-50 border border-rose-100 text-rose-800 text-sm">
          <AlertCircle className="shrink-0 mt-0.5" size={18} />
          <div>
            <p className="font-bold">{error}</p>
            <button
              type="button"
              onClick={load}
              className="mt-2 text-xs font-bold underline text-rose-900"
            >
              Thử lại
            </button>
          </div>
        </div>
      )}

      {loading && !dash ? (
        <div className="flex flex-col items-center justify-center py-24 text-gray-500 gap-3">
          <Loader2 className="animate-spin text-red-600" size={40} />
          <span className="text-sm font-bold">Đang tải dữ liệu…</span>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {kpi.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-[2rem] p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all"
              >
                <div className="flex justify-between items-start gap-2">
                  <div className={`p-3 rounded-2xl ${item.color} ${item.text}`}>
                    <item.icon size={24} />
                  </div>
                  <span
                    className="text-[10px] font-bold text-slate-500 bg-slate-50 px-2 py-1 rounded-lg text-right max-w-[12rem] leading-tight"
                    title={item.hint}
                  >
                    {item.hint}
                  </span>
                </div>
                <div className="mt-4">
                  <div className="text-3xl font-black text-gray-800 tracking-tight">
                    {item.value}
                  </div>
                  <div className="text-xs font-bold text-gray-400 uppercase tracking-[0.15em] mt-1">
                    {item.label}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-xs text-gray-500 font-medium">
            Bài học (trong phạm vi khóa có lớp lọc được):{" "}
            <span className="font-black text-gray-700">{stats?.lessons_total ?? "—"}</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-white rounded-[2.5rem] p-8 shadow-sm border border-gray-100">
              <h2 className="text-lg font-black text-gray-800 uppercase tracking-tight mb-6 flex items-center gap-2">
                <TrendingUp className="text-red-600" size={20} />
                Đăng ký lớp theo tháng ({selectedYear})
              </h2>
              <div className="h-[300px] w-full">
                {enrollmentByMonth.some((d) => d.students > 0) ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={enrollmentByMonth}>
                      <defs>
                        <linearGradient id="colorStudents" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#ef4444" stopOpacity={0.12} />
                          <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis
                        dataKey="label"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 11, fontWeight: 600, fill: "#94a3b8" }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 11, fontWeight: 600, fill: "#94a3b8" }}
                        allowDecimals={false}
                      />
                      <Tooltip />
                      <Area
                        type="monotone"
                        dataKey="students"
                        stroke="#ef4444"
                        strokeWidth={2}
                        fillOpacity={1}
                        fill="url(#colorStudents)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-sm text-gray-400 font-medium">
                    Chưa có đăng ký trong năm đã chọn (theo bộ lọc)
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-[2.5rem] p-8 shadow-sm border border-gray-100">
              <h2 className="text-lg font-black text-gray-800 uppercase tracking-tight mb-6">
                Lớp theo môn học
              </h2>
              <div className="h-[300px] w-full flex items-center">
                {bySubject.length > 0 ? (
                  <>
                    <ResponsiveContainer width="55%" height="100%">
                      <PieChart>
                        <Pie
                          data={bySubject}
                          cx="50%"
                          cy="50%"
                          innerRadius={68}
                          outerRadius={92}
                          paddingAngle={4}
                          dataKey="value"
                          nameKey="name"
                        >
                          {bySubject.map((_, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={COLORS[index % COLORS.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="flex flex-col gap-2 pl-2 overflow-y-auto max-h-[280px] flex-1">
                      {bySubject.map((item, index) => (
                        <div key={item.name} className="flex items-center gap-2 text-left">
                          <div
                            className="w-2.5 h-2.5 rounded-full shrink-0"
                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                          />
                          <span className="text-[11px] font-bold text-gray-600 leading-snug">
                            {item.name}{" "}
                            <span className="text-gray-400">({item.value})</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="w-full flex items-center justify-center text-sm text-gray-400 font-medium">
                    Không có lớp trong phạm vi lọc
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 pb-10">
            <div className="lg:col-span-2 bg-white rounded-[2.5rem] p-8 shadow-sm border border-gray-100">
              <h2 className="text-lg font-black text-gray-800 uppercase tracking-tight mb-6">
                Đăng ký gần đây
              </h2>
              {recent.length > 0 ? (
                <div className="space-y-4">
                  {recent.map((row, idx) => (
                    <div
                      key={`${row.student_name}-${row.enrolled_at}-${idx}`}
                      className="flex items-center justify-between p-4 rounded-2xl hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100"
                    >
                      <div className="flex items-center gap-4 min-w-0">
                        <div className="w-11 h-11 rounded-xl bg-gray-100 flex items-center justify-center font-black text-gray-500 shrink-0">
                          {(row.student_name || "?").charAt(0)}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-black text-gray-800 truncate">
                            {row.student_name}
                          </div>
                          <p className="text-xs text-gray-500 font-medium truncate">
                            {row.class_name} — {row.course_name}
                          </p>
                        </div>
                      </div>
                      <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest shrink-0 ml-2">
                        {fmtDT(row.enrolled_at)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 font-medium">Chưa có đăng ký trong phạm vi lọc</p>
              )}
            </div>

            <div className="bg-red-700 rounded-[2.5rem] p-8 text-white shadow-xl shadow-red-100 relative overflow-hidden flex flex-col">
              <div className="relative z-10 flex-1 flex flex-col">
                <h2 className="text-lg font-black uppercase tracking-tight mb-6 flex items-center gap-2">
                  <Calendar size={20} /> Lịch hôm nay
                </h2>
                <p className="text-[10px] font-bold opacity-80 uppercase tracking-widest mb-4">
                  {branchLabel} · Các lớp có buổi theo lịch chuẩn (T2…CN)
                </p>
                <div className="space-y-3 flex-1">
                  {scheduleToday.length > 0 ? (
                    scheduleToday.map((item) => (
                      <div
                        key={`${item.class_id}-${item.time}`}
                        className="bg-white/10 backdrop-blur-md p-4 rounded-2xl border border-white/10"
                      >
                        <div className="flex justify-between font-black text-[10px] tracking-widest opacity-80 uppercase mb-1 gap-2">
                          <span>{item.time}</span>
                          <span className="truncate">{item.room}</span>
                        </div>
                        <div className="font-bold text-sm leading-snug">{item.title}</div>
                        <div className="text-[10px] opacity-80 mt-1">{item.course}</div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm font-medium opacity-90">
                      Không có lớp nào trùng thứ trong tuần hôm nay (theo lịch lớp) trong phạm vi lọc.
                    </p>
                  )}
                </div>
                <Link
                  to="/elearning/calendar"
                  className="w-full mt-6 py-4 bg-white text-red-700 rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-red-50 transition-colors text-center"
                >
                  Mở lịch đào tạo
                </Link>
              </div>
              <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-white/5 rounded-full pointer-events-none" />
            </div>
          </div>

          <div className="text-center py-6 text-gray-400 text-xs font-bold uppercase tracking-widest">
            {branchLabel} · Năm {selectedYear}
            {loading ? " · Đang cập nhật…" : ""}
          </div>
        </>
      )}
    </div>
  );
}
